import { useState, useEffect } from "react";
import axios, { AxiosResponse, CanceledError } from "axios";

export const useDataApi = (
  initialUrl?: string,
  initialData?: any
): {
  data: any;
  isLoading: boolean;
  error: any;
  setUrl: Function;
  _get: Function;
  post: Function;
  put: Function;
  patch: Function;
  _delete: Function;
  response: AxiosResponse | undefined;
} => {
  const [data, setData] = useState(initialData);
  const [url, setUrl] = useState(initialUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [response, setResponse] = useState<AxiosResponse | undefined>();

  useEffect(() => {
    if (!url) {
      return;
    }

    let isActive = true;

    const controller = new AbortController();

    const fetchData = async () => {
      setError(undefined);
      setIsLoading(true);

      try {
        const response = await axios.get(url as string, {
          signal: controller.signal,
        });
        if (isActive) {
          setData(response.data);
        }
      } catch (_error: any) {
        if (!(_error instanceof CanceledError)) {
          setData(undefined);
          setError(_error);
        }
      } finally{
        setIsLoading(false);
      }

    };

    fetchData();

    return () => {
      controller.abort();
      isActive = false;
    };
  }, [url]);

  const _get = async (url: string) => {
    setIsLoading(true);
    try {
      setError(undefined);
      setResponse(undefined);
      const response = await axios.get(url);
      setResponse(response);
    } catch (_error: any) {
      setError(_error);
    }finally {
      setIsLoading(false);
    }
  };

  const post = async (url: string, data: any) => {
    setIsLoading(true);
    try {
      setError(undefined);
      setResponse(undefined);
      const response = await axios.post(url, data);
      setResponse(response);
    } catch (_error: any) {
      setError(_error);
    } finally {
      setIsLoading(false);
    }
  };

  const put = async (url: string, data: any) => {
    setIsLoading(true);
    try {
      setError(undefined);
      setResponse(undefined);
      const response = await axios.put(url, data);
      setResponse(response);
    } catch (_error: any) {
      setError(_error);
    } finally {
      setIsLoading(false);
    }
  };

  const patch = async (url: string, data: any) => {
    setIsLoading(true);
    try {
      setError(undefined);
      setResponse(undefined);
      const response = await axios.patch(url, data);
      setResponse(response);
    } catch (_error: any) {
      setError(_error);
    } finally {
      setIsLoading(false);
    }
  };

  const _delete = async (url: string) => {
    setIsLoading(true);
    try {
      setError(undefined);
      setResponse(undefined);
      const response = await axios.delete(url);
      setResponse(response);
    } catch (_error: any) {
      setError(_error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    data,
    isLoading,
    error,
    setUrl,
    _get,
    post,
    put,
    patch,
    _delete,
    response,
  };
};
